import { useMemo, useState } from "react";
import { galleryCategories, galleryItems } from "@/mocks/gallery";
import Reveal from "@/pages/home/components/Reveal";

export default function CategoryBrowser() {
  const [active, setActive] = useState("all");

  const labelOf = (id: string) =>
    galleryCategories.find((c) => c.id === id)?.label ?? "图片";

  const visibleItems = useMemo(
    () => (active === "all" ? galleryItems : galleryItems.filter((item) => item.category === active)),
    [active]
  );

  return (
    <section id="categories" className="py-16 md:py-24 bg-background-100">
      <div className="w-full px-4 md:px-8">
        <Reveal>
          <div className="max-w-3xl">
            <span className="text-xs font-label tracking-widest text-primary-600 uppercase">Browse by Theme</span>
            <h2 className="mt-2 font-heading text-2xl md:text-4xl font-semibold text-foreground-950">主题分类浏览</h2>
            <p className="mt-3 text-sm md:text-base text-foreground-700">
              点击标签即可在本页即时筛选，只保留对应类别的条目；切换回「全部」可以一次查看所有已整理的内容。每张卡片都标注了分辨率，方便判断适合的设备场景。
            </p>
          </div>
        </Reveal>

        <Reveal delay={80}>
          <div className="mt-8 flex flex-wrap gap-2">
            {galleryCategories.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => setActive(cat.id)}
                aria-pressed={active === cat.id}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  active === cat.id
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-50 border border-background-300/70 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {visibleItems.map((item, index) => (
            <Reveal key={item.id} delay={index * 40}>
              <article className="group h-full rounded-lg overflow-hidden bg-background-50 border border-background-200/80 hover:border-primary-300 transition-colors">
                <div className="relative w-full h-44 md:h-56 overflow-hidden">
                  <img
                    src={item.image}
                    alt={`${item.title} - ${labelOf(item.category)}图片`}
                    title={`${item.title} · 年轻的岳母${labelOf(item.category)}`}
                    className="w-full h-full object-top transition-transform duration-700 group-hover:scale-105"
                  />
                  <span className="absolute bottom-2 left-2 px-2 py-0.5 rounded-full bg-background-50/90 text-[10px] text-foreground-700">
                    {labelOf(item.category)}
                  </span>
                </div>
                <div className="p-4">
                  <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-950">{item.title}</h3>
                  <p className="mt-1.5 text-xs leading-relaxed text-foreground-600 line-clamp-2">{item.desc}</p>
                  <div className="mt-3 flex items-center gap-1.5 text-[11px] text-foreground-500">
                    <i className="ri-aspect-ratio-line"></i>
                    {item.resolution}
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        {visibleItems.length === 0 && (
          <p className="mt-8 text-sm text-foreground-600">该分类暂无内容，试试其他标签。</p>
        )}
      </div>
    </section>
  );
}